import { v4 as uuidv4 } from "uuid";
import {
  getMarginOptions,
  getPaddingOptions,
} from "@/react/components/shared/BuilderUI/utils/boxModelStyleHelpers";
import { getBackgroundColorOptions } from "@/react/components/shared/BuilderUI/utils/buttonStyleHelpers";
import { getBorderRadiusOptions } from "@/react/components/shared/BuilderUI/utils/imgStyleHelpers";
import { getSizingOptions } from "@/react/components/shared/BuilderUI/utils/sizeStyleHelpers";
import {
  getColorOptions,
  getTextAlignOptions,
} from "@/react/components/shared/BuilderUI/utils/textStyleHelpers";
import { defaultData as defaultPData } from "@/react/components/shared/Emails/VisualEmailBuilder/Blocks/Content/PBlock/P1BlockDefaultData";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { createImageBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/mediaBlockHelpers";
import {
  createH2Block,
  createPBlock,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textBlocksHelpers";
import { defaultData as defaultImageData } from "@circle-react-shared/Emails/VisualEmailBuilder/Blocks/Content/ImgBlock/ImgBlockDefaultData";
import placeholderImage from "@circle-react-shared/Emails/VisualEmailBuilder/Blocks/Content/ImgBlock/img_preview_dark.png";
import { defaultData as defaultH2Data } from "@circle-react-shared/Emails/VisualEmailBuilder/Blocks/Headings/H2Block/H2BlockDefaultData";
import { defaultStyles } from "./defaultStyles";

const createImageBlockWithHeight = (height: string) => ({
  sizing: getSizingOptions({
    defaultValue: {
      width: "100%",
      height,
      minWidth: "auto",
      minHeight: "auto",
      maxWidth: "100%",
      maxHeight: height,
    },
  }),
  borderRadius: getBorderRadiusOptions({
    defaultValue: {
      border_left_top_radius: 8,
      border_left_bottom_radius: 8,
      border_right_top_radius: 8,
      border_right_bottom_radius: 8,
    },
  }),
  margin: getMarginOptions({
    defaultValue: {
      marginTop: "12",
      marginBottom: "12",
      marginLeft: "24",
      marginRight: "24",
    },
  }),
});

export const defaultData: BaseBlock = {
  id: uuidv4(),
  type: "ImageGalleryBlock3",
  props: {
    headerContent: {
      prop: "children",
      type: "children",
      value: [
        createH2Block({
          text: "Image Gallery",
          baseData: defaultH2Data,
          styles: {
            textAlign: getTextAlignOptions("center"),
            color: getColorOptions(defaultStyles.color),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "12",
                marginBottom: "12",
                marginLeft: "24",
                marginRight: "24",
              },
            }),
          },
        }),
        createPBlock({
          text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
          baseData: defaultPData,
          styles: {
            textAlign: getTextAlignOptions("center"),
            color: getColorOptions(defaultStyles.color),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "12",
                marginBottom: "48",
                marginLeft: "24",
                marginRight: "24",
              },
            }),
          },
        }),
      ],
    },
    // Column 1: tall, short, medium
    imageNode1: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          alt: "Gallery image 1",
          placeholder: placeholderImage,
          baseData: defaultImageData,
          styles: {
            ...createImageBlockWithHeight("306px"),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "12",
                marginBottom: "12",
                marginLeft: "24",
                marginRight: "8",
              },
            }),
          },
        }),
      ],
    },
    imageNode2: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          alt: "Gallery image 2",
          placeholder: placeholderImage,
          baseData: defaultImageData,
          styles: {
            ...createImageBlockWithHeight("108px"),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "0",
                marginBottom: "0",
                marginLeft: "24",
                marginRight: "8",
              },
            }),
          },
        }),
      ],
    },
    imageNode3: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          alt: "Gallery image 3",
          placeholder: placeholderImage,
          baseData: defaultImageData,
          styles: {
            ...createImageBlockWithHeight("180px"),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "12",
                marginBottom: "12",
                marginLeft: "24",
                marginRight: "8",
              },
            }),
          },
        }),
      ],
    },
    // Column 2: short, tall, medium
    imageNode4: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          alt: "Gallery image 4",
          placeholder: placeholderImage,
          baseData: defaultImageData,
          styles: {
            ...createImageBlockWithHeight("108px"),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "12",
                marginBottom: "12",
                marginLeft: "8",
                marginRight: "8",
              },
            }),
          },
        }),
      ],
    },
    imageNode5: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          alt: "Gallery image 5",
          placeholder: placeholderImage,
          baseData: defaultImageData,
          styles: {
            ...createImageBlockWithHeight("306px"),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "0",
                marginBottom: "0",
                marginLeft: "8",
                marginRight: "8",
              },
            }),
          },
        }),
      ],
    },
    imageNode6: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          alt: "Gallery image 6",
          placeholder: placeholderImage,
          baseData: defaultImageData,
          styles: {
            ...createImageBlockWithHeight("180px"),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "12",
                marginBottom: "12",
                marginLeft: "8",
                marginRight: "8",
              },
            }),
          },
        }),
      ],
    },
    // Column 3: short, medium, tall
    imageNode7: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          alt: "Gallery image 7",
          placeholder: placeholderImage,
          baseData: defaultImageData,
          styles: {
            ...createImageBlockWithHeight("108px"),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "12",
                marginBottom: "12",
                marginLeft: "8",
                marginRight: "24",
              },
            }),
          },
        }),
      ],
    },
    imageNode8: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          alt: "Gallery image 8",
          placeholder: placeholderImage,
          baseData: defaultImageData,
          styles: {
            ...createImageBlockWithHeight("180px"),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "0",
                marginBottom: "0",
                marginLeft: "8",
                marginRight: "24",
              },
            }),
          },
        }),
      ],
    },
    imageNode9: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          alt: "Gallery image 9",
          placeholder: placeholderImage,
          baseData: defaultImageData,
          styles: {
            ...createImageBlockWithHeight("306px"),
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "12",
                marginBottom: "12",
                marginLeft: "8",
                marginRight: "24",
              },
            }),
          },
        }),
      ],
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        backgroundColor: getBackgroundColorOptions({
          defaultValue: defaultStyles.backgroundColor,
        }),
        textAlign: getTextAlignOptions("center"),
        padding: getPaddingOptions({
          defaultValue: defaultStyles.padding,
        }),
        margin: getMarginOptions({
          defaultValue: defaultStyles.margin,
        }),
      },
    },
  },
};
